/**
 * Ritual schools — `/v1/admin/ritual-schools` (content 08 §4.2, data 08 §3.13).
 *
 * Shakhas, grihya sutras and regional paddhatis. The server lints each write, refuses a parent
 * that would loop the tree, and audits; this file only moves the request.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type RitualSchoolRow = components['schemas']['RitualSchoolRow'];
export type RitualSchoolIn = components['schemas']['RitualSchoolIn'];
export type RitualSchoolPatch = components['schemas']['RitualSchoolPatch'];
export type RitualSchoolKind = RitualSchoolIn['school_kind'];
type RitualSchoolList = components['schemas']['RitualSchoolList'];

export interface SchoolQuery extends Record<string, unknown> {
  q?: string;
  kind?: RitualSchoolKind;
  include_inactive?: boolean;
}

export function schoolSearch(params: SchoolQuery): string {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== false) out.set(key, String(value));
  }
  const text = out.toString();
  return text ? `?${text}` : '';
}

export function useRitualSchools(params: SchoolQuery) {
  return useQuery({
    queryKey: keys.ritualSchools.list(params),
    queryFn: () =>
      authedClient.request<RitualSchoolList>(`/v1/admin/ritual-schools${schoolSearch(params)}`),
  });
}

export function useCreateRitualSchool() {
  const client = useQueryClient();
  return useMutation<RitualSchoolRow, unknown, RitualSchoolIn>({
    mutationFn: (body) =>
      authedClient.request<RitualSchoolRow>('/v1/admin/ritual-schools', { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.ritualSchools.all() }),
  });
}

export function useUpdateRitualSchool() {
  const client = useQueryClient();
  return useMutation<RitualSchoolRow, unknown, { id: number; patch: RitualSchoolPatch }>({
    mutationFn: ({ id, patch }) =>
      authedClient.request<RitualSchoolRow>(`/v1/admin/ritual-schools/${id}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.ritualSchools.all() }),
  });
}
